import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Pagination,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar
} from '@mui/material';
import {
  getBlogPosts,
  createPreBuiltMergedBlog,
  getAllPreBuiltMergedBlogs,
  getBlogsBySubcategory,
  generateDynamicMergedBlog,
  getMyBlogPosts,
  getBlogPerformanceStats,
  updateBlogPost,
  getBlogPostById
} from '../services/blogService';
import categoryService from '../services/categoryService';
import { useAuth } from '../context/AuthContext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import DashboardLayout from '../components/DashboardLayout';
import BlogDialog from '../components/BlogDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  KeyboardArrowDown,
  ViewList,
  ExpandMore,
  OpenInNew,
  ArrowUpward,
  ArrowDownward,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

// Utility function to map MongoDB IDs to 5-digit strings
const mapIdToShortString = (id) => {
  if (!id || id === 'N/A') return 'N/A';
  const idStr = typeof id === 'object' ? (id._id || id).toString() : id.toString();
  let hash = 0;
  for (let i = 0; i < idStr.length; i++) {
    const char = idStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const shortId = Math.abs(hash) % 90000 + 10000;
  return shortId.toString();
};

// Calendar helper functions
const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month, 1).getDay();
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return 'Select date range';
  if (!startDate) return `Until ${formatDateDisplay(endDate)}`;
  if (!endDate) return `From ${formatDateDisplay(startDate)}`;
  return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1 === date2;
};

const isDateInRange = (date, startDate, endDate) => {
  if (!startDate && !endDate) return false;
  if (startDate && endDate) {
    return date >= startDate && date <= endDate;
  } else if (startDate) {
    return date >= startDate;
  } else if (endDate) {
    return date <= endDate;
  }
  return false;
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Blog Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'view', 'edit'
  const [selectedPost, setSelectedPost] = useState(null);

  // Tab state - default to Pre-built Blog tab (1) for admin portal
  const [tabValue, setTabValue] = useState(isAdmin ? 1 : 0);

  // Pre-built blog state
  const [categories, setCategories] = useState([]);
  const [preBuiltCategory, setPreBuiltCategory] = useState('');
  const [preBuiltSubcategory, setPreBuiltSubcategory] = useState('');
  // Removed blogMergeLimit - all blogs in subcategory are now included for management
  const [rankingCriteria, setRankingCriteria] = useState('views');
  const [preBuiltLoading, setPreBuiltLoading] = useState(false);
  const [mergedBlogs, setMergedBlogs] = useState([]);
  const [loadingMergedBlogs, setLoadingMergedBlogs] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]); // Store all blog posts for merged blog display
  const [blogPerformanceData, setBlogPerformanceData] = useState({}); // Map of blogId -> performance data
  const [mergedBlogPerformanceData, setMergedBlogPerformanceData] = useState({}); // Map of mergedBlogId -> performance data
  const [blogPageStats, setBlogPageStats] = useState({}); // Map of blogId -> page-level stats array
  const [sourceBlogMap, setSourceBlogMap] = useState({}); // Map of blogId -> full blog object with content
  const [pausedBlogs, setPausedBlogs] = useState({}); // Map of mergedBlogId -> Set of paused blog IDs
  const [expandedStatsDialog, setExpandedStatsDialog] = useState(null); // { blogId, blogTitle, offerStats, views, clicks, sales, revenue } or null
  const [mergedBlogsLimit, setMergedBlogsLimit] = useState(3); // Limit for merged blogs display (horizontal)
  const [blogsPerMergedBlogLimit, setBlogsPerMergedBlogLimit] = useState({}); // Map of mergedBlogId -> limit for blogs within that merged blog (vertical)
  const [mergedBlogsStartDate, setMergedBlogsStartDate] = useState(''); // Start date for filtering merged blogs
  const [mergedBlogsEndDate, setMergedBlogsEndDate] = useState(''); // End date for filtering merged blogs
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [dateRangePickerAnchor, setDateRangePickerAnchor] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [startMonth, setStartMonth] = useState(new Date());
  const [endMonth, setEndMonth] = useState(new Date());
  const [includeToday, setIncludeToday] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load categories for prebuilt blog tab
  useEffect(() => {
    if (isAdmin && tabValue === 1) {
      const loadCategories = async () => {
        try {
          const categoriesData = await categoryService.getCategoriesForSelect();
          setCategories(categoriesData);
        } catch (error) {
          console.error('Error loading categories:', error);
        }
      };
      loadCategories();
    }
  }, [isAdmin, tabValue]);

  // Load merged blogs when prebuilt tab is active
  useEffect(() => {
    if (isAdmin && tabValue === 1) {
      fetchMergedBlogs();
    }
  }, [isAdmin, tabValue]);

  useEffect(() => {
    // Only fetch blog posts if not admin or if tab 0 is active (for non-admin users)
    if (!isAdmin && tabValue === 0) {
      const fetchPosts = async () => {
        try {
          setLoading(true);
          const data = await getBlogPosts(page, 6, selectedTag);
          setPosts(data.posts);
          setTotalPages(data.totalPages);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching blog posts:', err);
          setError('Failed to load blog posts. Please try again later.');
          setLoading(false);
        }
      };

      fetchPosts();
    }
  }, [page, selectedTag, tabValue, isAdmin]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag === selectedTag ? null : tag);
    setPage(1);
  };

  const handleCreatePost = () => {
    setDialogMode('create');
    setSelectedPost(null);
    setDialogOpen(true);
  };

  const handleViewPost = (post) => {
    setDialogMode('view');
    setSelectedPost(post);
    setDialogOpen(true);
  };

  const handleEditPost = (post) => {
    setDialogMode('edit');
    setSelectedPost(post);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPost(null);
  };

  const handleDialogSuccess = async () => {
    // Refresh posts after create/edit
    try {
      const data = await getBlogPosts(page, 6, selectedTag);
      setPosts(data.posts);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error('Error refreshing posts:', err);
    }
  };

  // Pre-built blog functions
  const handlePreBuiltCategoryChange = (event) => {
    const categoryId = event.target.value;
    setPreBuiltCategory(categoryId);
    setPreBuiltSubcategory('');
  };

  const handlePreBuiltSubcategoryChange = (event) => {
    const subcategoryId = event.target.value;
    setPreBuiltSubcategory(subcategoryId);
  };

  // Reload a single merged blog
  const reloadMergedBlog = async (mergedBlogId) => {
    try {
      const mergedBlogIdStr = mergedBlogId?.toString();
      if (!mergedBlogIdStr) return;

      const updatedBlog = await getBlogPostById(mergedBlogIdStr);

      setMergedBlogs(prev => prev.map(mb =>
        mb._id?.toString() === mergedBlogIdStr ? updatedBlog : mb
      ));

      if (updatedBlog.pausedBlogIds && Array.isArray(updatedBlog.pausedBlogIds) && updatedBlog.pausedBlogIds.length > 0) {
        setPausedBlogs(prev => ({
          ...prev,
          [mergedBlogIdStr]: new Set(
            updatedBlog.pausedBlogIds.map(id => {
              const idStr = typeof id === 'object' ? id._id || id : id;
              return idStr?.toString();
            }).filter(Boolean)
          )
        }));
      } else {
        setPausedBlogs(prev => {
          const newState = { ...prev };
          if (newState[mergedBlogIdStr]) {
            delete newState[mergedBlogIdStr];
          }
          return newState;
        });
      }

      if (updatedBlog.sourceBlogIds && Array.isArray(updatedBlog.sourceBlogIds)) {
        const sourceBlogPromises = updatedBlog.sourceBlogIds.map(async (id) => {
          const blogId = typeof id === 'object' ? id._id || id : id;
          const blogIdStr = blogId?.toString();
          if (!blogIdStr) return null;

          if (sourceBlogMap[blogIdStr]) {
            return null;
          }

          try {
            const blog = await getBlogPostById(blogIdStr);
            return { blogId: blogIdStr, blog };
          } catch (error) {
            console.error(`Error fetching source blog ${blogIdStr}:`, error);
            return null;
          }
        });

        const sourceBlogResults = await Promise.all(sourceBlogPromises);
        const newSourceBlogMap = { ...sourceBlogMap };
        sourceBlogResults.forEach((result) => {
          if (result && result.blog) {
            newSourceBlogMap[result.blogId] = result.blog;
          }
        });

        if (Object.keys(newSourceBlogMap).length > Object.keys(sourceBlogMap).length) {
          setSourceBlogMap(newSourceBlogMap);
        }
      }
    } catch (error) {
      console.error('Error reloading merged blog:', error);
      showSnackbar('Failed to reload snake', 'error');
    }
  };

  // Fetch all merged blogs (both from BlogPost and PreBuiltMergedBlog collections)
  const fetchMergedBlogs = async () => {
    setLoadingMergedBlogs(true);
    try {
      // Fetch merged blogs from BlogPost collection (legacy)
      // Note: getMyBlogPosts already includes pre-built merged blogs, so we need to deduplicate
      const postsData = await getMyBlogPosts({ limit: 100 });
      const mergedFromBlogPost = (postsData.posts || []).filter(post => post.isMergedBlog && !post.isPreBuiltMergedBlog);

      // Fetch pre-built merged blogs from PreBuiltMergedBlog collection
      let preBuiltMerged = [];
      try {
        const preBuiltData = await getAllPreBuiltMergedBlogs();
        preBuiltMerged = preBuiltData.blogs || preBuiltData || [];
        preBuiltMerged = preBuiltMerged.map(blog => ({
          ...blog,
          isMergedBlog: true,
          isPreBuiltMergedBlog: true
        }));
      } catch (error) {
        console.warn('Error fetching pre-built merged blogs:', error);
      }

      // Combine and deduplicate by _id to avoid duplicates
      const mergedMap = new Map();

      // Add legacy merged blogs from BlogPost
      mergedFromBlogPost.forEach(blog => {
        const id = blog._id?.toString();
        if (id) {
          mergedMap.set(id, blog);
        }
      });

      // Add pre-built merged blogs (will overwrite if duplicate ID exists)
      preBuiltMerged.forEach(blog => {
        const id = blog._id?.toString();
        if (id) {
          mergedMap.set(id, blog);
        }
      });

      // Convert map back to array
      const merged = Array.from(mergedMap.values());

      setMergedBlogs(merged);

      // Initialize paused blogs state from merged blogs
      const initialPausedBlogs = {};
      merged.forEach(mergedBlog => {
        if (mergedBlog.pausedBlogIds && Array.isArray(mergedBlog.pausedBlogIds) && mergedBlog.pausedBlogIds.length > 0) {
          const mergedBlogIdStr = mergedBlog._id?.toString();
          initialPausedBlogs[mergedBlogIdStr] = new Set(
            mergedBlog.pausedBlogIds.map(id => {
              const idStr = typeof id === 'object' ? id._id || id : id;
              return idStr?.toString();
            }).filter(Boolean)
          );
        }
      });
      if (Object.keys(initialPausedBlogs).length > 0) {
        setPausedBlogs(initialPausedBlogs);
      }

      // Collect all unique source blog IDs
      const allSourceBlogIds = new Set();
      merged.forEach(mergedBlog => {
        if (mergedBlog.sourceBlogIds && Array.isArray(mergedBlog.sourceBlogIds)) {
          mergedBlog.sourceBlogIds.forEach(id => {
            const blogId = typeof id === 'object' ? id._id || id : id;
            if (blogId) allSourceBlogIds.add(blogId.toString());
          });
        }
      });

      // Fetch all source blog details
      const sourceBlogPromises = Array.from(allSourceBlogIds).map(async (blogId) => {
        try {
          const blog = await getBlogPostById(blogId);
          return { blogId, blog };
        } catch (error) {
          if (error.response?.status === 404) {
            console.warn(`Source blog ${blogId} not found (may have been deleted)`);
          } else {
            console.error(`Error fetching source blog ${blogId}:`, error);
          }
          return { blogId, blog: null };
        }
      });

      const sourceBlogResults = await Promise.all(sourceBlogPromises);
      const newSourceBlogMap = {};
      sourceBlogResults.forEach(({ blogId, blog }) => {
        if (blog) {
          newSourceBlogMap[blogId] = blog;
        }
      });

      setSourceBlogMap(newSourceBlogMap);

      // Update blogPosts state with source blogs for display
      const updatedBlogPosts = [...blogPosts];
      sourceBlogResults.forEach(({ blogId, blog }) => {
        if (blog && !updatedBlogPosts.find(bp => bp._id === blogId)) {
          updatedBlogPosts.push(blog);
        }
      });
      setBlogPosts(updatedBlogPosts);

      // Fetch performance data for each unique affiliate
      const affiliateIds = new Set();
      merged.forEach(mergedBlog => {
        if (mergedBlog.author) {
          const authorId = typeof mergedBlog.author === 'object' ? mergedBlog.author._id || mergedBlog.author : mergedBlog.author;
          if (authorId) affiliateIds.add(authorId.toString());
        }
      });

      sourceBlogResults.forEach(({ blog }) => {
        if (blog && blog.author) {
          const authorId = typeof blog.author === 'object' ? blog.author._id || blog.author : blog.author;
          if (authorId) affiliateIds.add(authorId.toString());
        }
      });

      // Fetch performance data for each affiliate
      const performancePromises = Array.from(affiliateIds).map(async (affiliateId) => {
        try {
          const perfData = await getBlogPerformanceStats(affiliateId);
          return { affiliateId, perfData };
        } catch (error) {
          console.error(`Error fetching performance for affiliate ${affiliateId}:`, error);
          return { affiliateId, perfData: null };
        }
      });

      const performanceResults = await Promise.all(performancePromises);
      const perfMap = {};
      const mergedBlogPerfMap = {};
      const pageStatsMap = {};

      performanceResults.forEach(({ affiliateId, perfData }) => {
        if (perfData && perfData.stats) {
          if (perfData.stats.byBlog) {
            perfData.stats.byBlog.forEach(blogStat => {
              if (blogStat.blogPostId) {
                const blogIdStr = blogStat.blogPostId.toString();
                perfMap[blogIdStr] = blogStat;

                const mergedBlog = merged.find(mb => mb._id?.toString() === blogIdStr);
                if (mergedBlog) {
                  mergedBlogPerfMap[mergedBlog._id] = blogStat;
                }
              }
            });
          }

          if (perfData.stats.pageStatsByBlog && Array.isArray(perfData.stats.pageStatsByBlog)) {
            perfData.stats.pageStatsByBlog.forEach(blogPageStat => {
              if (blogPageStat.blogPostId && blogPageStat.pages) {
                const blogIdStr = blogPageStat.blogPostId.toString();
                pageStatsMap[blogIdStr] = blogPageStat.pages;
              }
            });
          }
        }
      });

      setBlogPerformanceData(perfMap);
      setMergedBlogPerformanceData(mergedBlogPerfMap);
      setBlogPageStats(pageStatsMap);
    } catch (error) {
      console.error('Error fetching merged blogs:', error);
      showSnackbar('Failed to load snakes', 'error');
      setMergedBlogs([]);
    } finally {
      setLoadingMergedBlogs(false);
    }
  };

  // Reorder blogs within a merged blog
  const handleReorderBlogsInMerged = async (mergedBlogId, newOrder) => {
    try {
      const mergedBlog = mergedBlogs.find(mb => mb._id === mergedBlogId);
      if (!mergedBlog) {
        showSnackbar('Snake not found', 'error');
        return;
      }

      const mergedBlogIdStr = mergedBlogId?.toString();
      const updatedBlogData = {
        sourceBlogIds: newOrder
      };

      await updateBlogPost(mergedBlogIdStr, updatedBlogData);
      showSnackbar('Blog order updated', 'success');

      await reloadMergedBlog(mergedBlogId);
    } catch (error) {
      console.error('Error reordering blogs in merged blog:', error);
      showSnackbar('Failed to reorder blogs: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Remove a blog from a merged blog
  const handleRemoveBlogFromMerged = async (mergedBlogId, sourceBlogId) => {
    try {
      const mergedBlog = mergedBlogs.find(mb => mb._id === mergedBlogId);
      if (!mergedBlog) {
        showSnackbar('Snake not found', 'error');
        return;
      }

      const mergedBlogIdStr = mergedBlogId?.toString();
      const sourceBlogIdStr = typeof sourceBlogId === 'object' ? (sourceBlogId._id || sourceBlogId)?.toString() : sourceBlogId?.toString();

      if (!sourceBlogIdStr) {
        showSnackbar('Invalid blog ID', 'error');
        return;
      }

      const currentSourceBlogIds = mergedBlog.sourceBlogIds || [];
      const updatedSourceBlogIds = currentSourceBlogIds.filter(id => {
        const idStr = typeof id === 'object' ? (id._id || id)?.toString() : id?.toString();
        return idStr !== sourceBlogIdStr;
      });

      // Also remove from pausedBlogIds if present
      const currentPausedBlogIds = mergedBlog.pausedBlogIds || [];
      const updatedPausedBlogIds = currentPausedBlogIds.filter(id => {
        const idStr = typeof id === 'object' ? (id._id || id)?.toString() : id?.toString();
        return idStr !== sourceBlogIdStr;
      });


      const updatedBlogData = {
        sourceBlogIds: updatedSourceBlogIds,
        pausedBlogIds: updatedPausedBlogIds,
        blogCount: updatedSourceBlogIds.length
      };

      await updateBlogPost(mergedBlogIdStr, updatedBlogData);
      showSnackbar('Blog removed from snake', 'success');

      await reloadMergedBlog(mergedBlogId);
    } catch (error) {
      console.error('Error removing blog from merged blog:', error);
      showSnackbar('Failed to remove blog: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const sortBlogsByPerformance = async (blogs, affiliateId, criteria) => {
    try {
      const performanceData = await getBlogPerformanceStats(affiliateId);
      const performanceMap = new Map();

      if (performanceData?.stats?.byBlog) {
        performanceData.stats.byBlog.forEach(blogStat => {
          let blogId = blogStat.blogPostId;
          if (blogId && typeof blogId !== 'string') {
            blogId = blogId.toString();
          }
          if (blogId) {
            const views = blogStat.totalViews || 0;
            const revenue = blogStat.totalRevenue || blogStat.revenue || 0;
            const revenuePerView = views > 0 ? revenue / views : 0;

            performanceMap.set(blogId, {
              views,
              revenue,
              revenuePerView
            });
          }
        });
      }

      return blogs.sort((a, b) => {
        let aId = a._id;
        let bId = b._id;
        if (aId && typeof aId !== 'string') {
          aId = aId.toString();
        }
        if (bId && typeof bId !== 'string') {
          bId = bId.toString();
        }

        const aStats = performanceMap.get(aId);
        const bStats = performanceMap.get(bId);

        if (aStats && bStats) {
          if (criteria === 'revenuePerView') {
            return bStats.revenuePerView - aStats.revenuePerView;
          } else {
            return bStats.views - aStats.views;
          }
        }
        if (aStats && !bStats) return -1;
        if (!aStats && bStats) return 1;
        return Math.random() - 0.5;
      });
    } catch (error) {
      console.error('Error fetching blog performance, randomizing instead:', error);
      return blogs.sort(() => Math.random() - 0.5);
    }
  };

  const concatenateBlogContents = (blogs) => {
    const pages = [];
    let pageId = 1;

    blogs.forEach((blog) => {
      try {
        let blogContent = blog.content;
        if (typeof blogContent === 'string') {
          try {
            blogContent = JSON.parse(blogContent);
          } catch (e) {
            blogContent = [{ id: `text-${pageId}`, type: 'text', content: blogContent }];
          }
        }

        if (Array.isArray(blogContent)) {
          if (blogContent[0]?.elements) {
            blogContent.forEach((page) => {
              if (page.elements && Array.isArray(page.elements)) {
                pages.push({
                  id: pageId++,
                  elements: [...page.elements]
                });
              }
            });
          } else if (blogContent[0]?.type) {
            pages.push({
              id: pageId++,
              elements: blogContent
            });
          }
        }
      } catch (e) {
        console.error('Error processing blog content:', e);
      }
    });

    return JSON.stringify(pages);
  };

  const generatePreBuiltBlog = async () => {
    if (!preBuiltCategory || !preBuiltSubcategory) {
      showSnackbar('Please select both category and subcategory', 'error');
      return;
    }

    setPreBuiltLoading(true);
    try {
      if (preBuiltCategory === 'all' || preBuiltSubcategory === 'all') {
        showSnackbar('Please select a specific category and subcategory to create a snake.', 'error');
        setPreBuiltLoading(false);
        return;
      }

      console.log('🔄 Generating dynamic merged blog server-side:', {
        categoryId: preBuiltCategory,
        subcategoryId: preBuiltSubcategory,
        rankingCriteria,
        note: 'All blogs in subcategory will be included (no limit)'
      });

      // Use server-side dynamic generation endpoint
      const result = await generateDynamicMergedBlog(
        preBuiltCategory,
        preBuiltSubcategory,
        {
          rankingCriteria
        }
      );

      if (result && result.success) {
        console.log('✅ Dynamic merged blog generated successfully:', {
          blogsIncluded: result.blogsIncluded,
          totalPages: result.totalPages,
          isUpdate: result.isUpdate
        });

        const action = result.isUpdate ? 'Updated' : 'Created';
        showSnackbar(
          `${action} snake with ${result.blogsIncluded} blog posts (${result.totalPages} pages)`,
          'success'
        );

        // Refresh merged blogs list
        await fetchMergedBlogs();

        // Reset form
        setPreBuiltCategory('');
        setPreBuiltSubcategory('');
      } else {
        showSnackbar(result?.message || 'Failed to generate snake', 'error');
      }
    } catch (error) {
      console.error('Error generating dynamic merged blog:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      showSnackbar('Failed to generate snake: ' + errorMessage, 'error');
    } finally {
      setPreBuiltLoading(false);
    }
  };

  // Extract all unique tags from posts
  const allTags = [...new Set(posts.flatMap(post => post.tags))];

  const blogActions = isAuthenticated ? (
    <Button
      onClick={handleCreatePost}
      variant="contained"
      startIcon={<AddIcon />}
    >
      New Post
    </Button>
  ) : null;

  return (
    <DashboardLayout title="Blog Optimizer" actions={blogActions} maxWidth="lg">
      <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 2 }}>

        {/* Tabs */}
        {isAdmin ? (
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>

          </Tabs>
        ) : (
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label="Blog Posts" />
          </Tabs>
        )}

        {!isAdmin && tabValue === 0 && (
          <>
            {/* Tags filter */}
            {allTags.length > 0 && (
              <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {allTags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    onClick={() => handleTagClick(tag)}
                    color={selectedTag === tag ? 'primary' : 'default'}
                    clickable
                  />
                ))}
                {selectedTag && (
                  <Button
                    size="small"
                    onClick={() => setSelectedTag(null)}
                    sx={{ ml: 1 }}
                  >
                    Clear filter
                  </Button>
                )}
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 4 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : posts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6">
                  {selectedTag
                    ? `No posts found with tag "${selectedTag}"`
                    : 'No blog posts yet'}
                </Typography>
                {isAuthenticated && (
                  <Button
                    onClick={handleCreatePost}
                    variant="contained"
                    sx={{ mt: 2 }}
                  >
                    Create the first post
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <Grid container spacing={4}>
                  {posts.map(post => (
                    <Grid item xs={12} sm={6} md={4} key={post._id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: 6
                          }
                        }}
                      >
                        {post.featuredImage && (
                          <CardMedia
                            component="img"
                            height="140"
                            image={post.featuredImage}
                            alt={post.title}
                          />
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h5" component="h2" noWrap>
                            {post.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              mb: 2
                            }}
                          >
                            {post.content.replace(/<[^>]*>/g, '').slice(0, 150)}...
                          </Typography>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                            <Typography variant="caption" color="text.secondary">
                              {post.createdAt && format(new Date(post.createdAt), 'MMM d, yyyy')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                onClick={() => handleViewPost(post)}
                                size="small"
                                startIcon={<VisibilityIcon />}
                              >
                                View
                              </Button>
                              {isAuthenticated && post.author?._id === user?.id && (
                                <Button
                                  onClick={() => handleEditPost(post)}
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                >
                                  Edit
                                </Button>
                              )}
                            </Box>
                          </Box>

                          {post.tags && post.tags.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {post.tags.map(tag => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleTagClick(tag);
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}

            {/* Blog Dialog - Advanced Popup */}
            <BlogDialog
              open={dialogOpen}
              onClose={handleCloseDialog}
              mode={dialogMode}
              blogPost={selectedPost}
              onSuccess={handleDialogSuccess}
            />
          </>
        )}

        {isAdmin && tabValue === 1 && (
          <Grid container spacing={3}>
            {/* Manual snake creation section hidden - snakes are now auto-generated */}

            {/* Merged Blogs Management Section */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6">
                    Blog Optimizer
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Date Range Filter - Calendar Picker */}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        setDateRangePickerAnchor(e.currentTarget);
                        setDateRangePickerOpen(true);
                        setTempStartDate(mergedBlogsStartDate || null);
                        setTempEndDate(mergedBlogsEndDate || null);
                        if (mergedBlogsStartDate) {
                          setStartMonth(new Date(mergedBlogsStartDate));
                        }
                        if (mergedBlogsEndDate) {
                          setEndMonth(new Date(mergedBlogsEndDate));
                        }
                      }}
                      endIcon={<KeyboardArrowDown />}
                      sx={{ minWidth: 250, justifyContent: 'space-between' }}
                    >
                      {formatDateRange(mergedBlogsStartDate, mergedBlogsEndDate)}
                    </Button>

                    {(mergedBlogsStartDate || mergedBlogsEndDate) && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setMergedBlogsStartDate('');
                          setMergedBlogsEndDate('');
                        }}
                      >
                        Clear
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchMergedBlogs}
                      disabled={loadingMergedBlogs}
                      startIcon={loadingMergedBlogs ? <CircularProgress size={16} /> : <ViewList />}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Box>

                {loadingMergedBlogs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (() => {
                  // Filter merged blogs by date range
                  let filteredMergedBlogs = mergedBlogs;

                  if (mergedBlogsStartDate || mergedBlogsEndDate) {
                    filteredMergedBlogs = mergedBlogs.filter(blog => {
                      const blogDateObj = new Date(blog.createdAt || blog._id);
                      const blogDateStr = formatDateForInput(blogDateObj);

                      if (mergedBlogsStartDate && mergedBlogsEndDate) {
                        return blogDateStr >= mergedBlogsStartDate && blogDateStr <= mergedBlogsEndDate;
                      } else if (mergedBlogsStartDate) {
                        return blogDateStr >= mergedBlogsStartDate;
                      } else if (mergedBlogsEndDate) {
                        return blogDateStr <= mergedBlogsEndDate;
                      }
                      return true;
                    });
                  }

                  return filteredMergedBlogs.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {mergedBlogsStartDate || mergedBlogsEndDate
                        ? 'No snakes found for the selected date range.'
                        : 'No snakes found. Generate a snake to create one.'}
                    </Alert>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: 2,
                        pb: 2,
                        '&::-webkit-scrollbar': {
                          height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: '#f1f1f1',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#888',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: '#555',
                          },
                        },
                      }}
                    >
                      {filteredMergedBlogs.slice(0, mergedBlogsLimit).map((mergedBlog) => {
                        const mergedBlogIdStr = mergedBlog._id?.toString();
                        const sourceBlogIds = mergedBlog.sourceBlogIds || [];
                        const blogsLimit = blogsPerMergedBlogLimit[mergedBlogIdStr] || 5;
                        const displayedBlogs = sourceBlogIds.slice(0, blogsLimit);
                        const hasMoreBlogs = sourceBlogIds.length > blogsLimit;
                        const perfData = mergedBlogPerformanceData[mergedBlog._id];

                        // Get category and subcategory names
                        let categoryId = null;
                        let subcategoryId = null;

                        if (mergedBlog.categoryId) {
                          categoryId = typeof mergedBlog.categoryId === 'object' && mergedBlog.categoryId !== null
                            ? (mergedBlog.categoryId._id || mergedBlog.categoryId)
                            : mergedBlog.categoryId;
                        }

                        if (mergedBlog.subcategoryId) {
                          subcategoryId = typeof mergedBlog.subcategoryId === 'object' && mergedBlog.subcategoryId !== null
                            ? (mergedBlog.subcategoryId._id || mergedBlog.subcategoryId)
                            : mergedBlog.subcategoryId;
                        }

                        // If merged blog doesn't have category/subcategory, try to get from first source blog
                        if (!categoryId && sourceBlogIds.length > 0) {
                          const firstSourceBlogId = sourceBlogIds[0];
                          const firstSourceBlogIdStr = typeof firstSourceBlogId === 'object' && firstSourceBlogId !== null
                            ? (firstSourceBlogId._id || firstSourceBlogId)?.toString()
                            : firstSourceBlogId?.toString();

                          if (firstSourceBlogIdStr) {
                            const firstSourceBlog = blogPosts.find(bp => {
                              const bpId = bp?._id?.toString();
                              return bpId === firstSourceBlogIdStr;
                            });

                            if (firstSourceBlog) {
                              if (firstSourceBlog.categoryId) {
                                categoryId = typeof firstSourceBlog.categoryId === 'object' && firstSourceBlog.categoryId !== null
                                  ? (firstSourceBlog.categoryId._id || firstSourceBlog.categoryId)
                                  : firstSourceBlog.categoryId;
                              }
                              if (firstSourceBlog.subcategoryId) {
                                subcategoryId = typeof firstSourceBlog.subcategoryId === 'object' && firstSourceBlog.subcategoryId !== null
                                  ? (firstSourceBlog.subcategoryId._id || firstSourceBlog.subcategoryId)
                                  : firstSourceBlog.subcategoryId;
                              }
                            }
                          }
                        }

                        const category = categoryId ? categories.find(c => {
                          if (!c || !c._id) return false;
                          const catId = typeof c._id === 'object' && c._id !== null
                            ? (c._id._id || c._id)
                            : c._id;
                          return catId?.toString() === categoryId?.toString();
                        }) : null;

                        const subcategory = category && subcategoryId ? category.subcategories?.find(s => {
                          if (!s || !s._id) return false;
                          const subId = typeof s._id === 'object' && s._id !== null
                            ? (s._id._id || s._id)
                            : s._id;
                          return subId?.toString() === subcategoryId?.toString();
                        }) : null;

                        // Calculate metrics
                        const views = perfData?.count || 0;
                        const clicks = perfData?.clicks || 0;
                        const sales = perfData?.sales || 0;
                        const revenue = perfData?.totalRevenue || perfData?.revenue || 0;
                        const ctr = clicks > 0 ? ((clicks / views) * 100).toFixed(2) : '0.00';
                        const cvr = clicks > 0 ? ((sales / clicks) * 100).toFixed(2) : '0.00';
                        const rpv = views > 0 ? (revenue / views).toFixed(2) : '0.00';

                        return (
                          <Card
                            key={mergedBlog._id}
                            sx={{
                              minWidth: 400,
                              maxWidth: 400,
                              flexShrink: 0,
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1.5 }}>
                              <Typography variant="h6" noWrap sx={{ mb: 0 }}>
                                {mergedBlog.title}
                              </Typography>

                              {/* Performance Metrics Section */}
                              <Box sx={{ mt: 0, pt: 0, borderTop: '1px solid #e0e0e0' }}>
                                <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { border: '1px solid #e0e0e0', padding: '4px 6px', textAlign: 'center', fontSize: '0.7rem' } }}>
                                  <TableBody>
                                    {/* Category and Sub Category - 2 columns in 2 rows */}
                                    <TableRow>
                                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem', padding: '4px 6px' }}>Category</TableCell>
                                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem', padding: '4px 6px' }}>Sub Category</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell colSpan={2} sx={{ fontSize: '0.7rem', padding: '4px 6px' }}>{category?.name || 'N/A'}</TableCell>
                                      <TableCell colSpan={2} sx={{ fontSize: '0.7rem', padding: '4px 6px' }}>{subcategory?.name || 'N/A'}</TableCell>
                                    </TableRow>

                                    {/* Engagement Metrics - 3 columns in 2 rows, spanning full width */}
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>Views</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>Clicks</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>CTR</TableCell>
                                      <TableCell sx={{ padding: 0, border: 'none', width: 0, minWidth: 0, maxWidth: 0 }}></TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{views.toLocaleString()}</TableCell>
                                      <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{clicks.toLocaleString()}</TableCell>
                                      <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', padding: '4px 6px' }}>{ctr}%</TableCell>
                                      <TableCell sx={{ padding: 0, border: 'none', width: 0, minWidth: 0, maxWidth: 0 }}></TableCell>
                                    </TableRow>

                                    {/* Conversion and Revenue Metrics - 4 columns in 2 rows */}
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>Sales</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>CVR</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>Revenue</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>RPV</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>{sales.toLocaleString()}</TableCell>
                                      <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>{cvr}%</TableCell>
                                      <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>${revenue.toFixed(2)}</TableCell>
                                      <TableCell sx={{ width: '25%', fontSize: '0.7rem', padding: '4px 6px' }}>${rpv}</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </Box>

                              {/* Containing Blogs List - Always Visible */}
                              <Box sx={{
                                mt: 2,
                                pt: 2,
                                borderTop: '1px solid #e0e0e0',
                                maxHeight: 400,
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': {
                                  width: '6px',
                                },
                                '&::-webkit-scrollbar-track': {
                                  background: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  background: 'rgba(0, 0, 0, 0.2)',
                                  borderRadius: '3px',
                                  '&:hover': {
                                    background: 'rgba(0, 0, 0, 0.3)',
                                  },
                                },
                                scrollbarWidth: 'thin',
                                scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                              }}>
                                {sourceBlogIds.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">
                                    No blogs in this snake.
                                  </Typography>
                                ) : (
                                  <Box>
                                    {displayedBlogs.map((sourceBlogId, index) => {
                                      const blogId = typeof sourceBlogId === 'object' ? sourceBlogId._id || sourceBlogId : sourceBlogId;
                                      const blogIdStr = blogId?.toString();
                                      const perfData = blogIdStr ? blogPerformanceData[blogIdStr] : null;

                                      const sourceBlog = blogPosts.find(bp => {
                                        const bpId = bp._id?.toString();
                                        return bpId === blogIdStr;
                                      });

                                      const blogThumbnail = sourceBlog?.featuredImage || null;

                                      let contentImage = null;
                                      if (!blogThumbnail && sourceBlog?.content) {
                                        try {
                                          let content;
                                          if (typeof sourceBlog.content === 'string') {
                                            // Only try to parse if it looks like JSON (starts with [ or {)
                                            const trimmed = sourceBlog.content.trim();
                                            if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                                              content = JSON.parse(sourceBlog.content);
                                            } else {
                                              // Not JSON, skip parsing
                                              content = null;
                                            }
                                          } else {
                                            content = sourceBlog.content;
                                          }

                                          if (content && Array.isArray(content)) {
                                            for (const page of content) {
                                              if (page.elements && Array.isArray(page.elements)) {
                                                const mediaElement = page.elements.find(el =>
                                                  el.type === 'media' && el.mediaUrl
                                                );
                                                if (mediaElement) {
                                                  contentImage = mediaElement.mediaUrl;
                                                  break;
                                                }
                                              }
                                            }
                                          }
                                        } catch (e) {
                                          // Silently ignore parse errors - content may not be JSON
                                        }
                                      }

                                      const thumbnailUrl = blogThumbnail || contentImage;

                                      const affiliateId = sourceBlog?.author?._id || sourceBlog?.author || 'N/A';
                                      const affiliateName = sourceBlog?.author?.name || sourceBlog?.author?.email || 'N/A';
                                      const affiliateUsername = sourceBlog?.author?.username || 'N/A';

                                      const isPaused = pausedBlogs[mergedBlogIdStr]?.has(blogIdStr) || false;

                                      return (
                                        <Card
                                          key={`${mergedBlogIdStr}-${blogIdStr || index}`}
                                          variant="outlined"
                                          sx={{
                                            mb: 1,
                                            width: '100%',
                                            overflow: 'visible',
                                            opacity: isPaused ? 0.5 : 1,
                                            backgroundColor: isPaused ? '#f5f5f5' : 'transparent',
                                            '&:hover': { backgroundColor: isPaused ? '#f5f5f5' : '#fafafa' }
                                          }}
                                        >
                                          {/* Affiliate and Blog Info Table */}
                                          <Box sx={{ p: 0, borderBottom: '1px solid #e0e0e0' }}>
                                            <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { border: '1px solid #e0e0e0', padding: '4px 6px', fontSize: '0.7rem' } }}>
                                              <TableBody>
                                                {/* Affiliate Information */}
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Affiliate ID</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Affiliate Name</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '33.33%', fontSize: '0.7rem' }}>Username</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                  <TableCell sx={{ width: '33.33%', fontSize: '0.7rem' }}>{mapIdToShortString(affiliateId)}</TableCell>
                                                  <TableCell sx={{ width: '33.33%', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={affiliateName}>{affiliateName}</TableCell>
                                                  <TableCell sx={{ width: '33.33%', fontSize: '0.7rem' }}>{affiliateUsername}</TableCell>
                                                </TableRow>

                                                {/* Blog Information */}
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem' }}>Blog ID</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.7rem' }} colSpan={2}>Blog Name</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.7rem' }}>{mapIdToShortString(blogIdStr)}</TableCell>
                                                  <TableCell colSpan={2} sx={{ wordBreak: 'break-word', whiteSpace: 'normal', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis' }} title={sourceBlog?.title || `Blog ${index + 1}`}>{sourceBlog?.title || `Blog ${index + 1}`}</TableCell>
                                                </TableRow>
                                              </TableBody>
                                            </Table>
                                          </Box>

                                          {/* Blog Preview and Stats Layout */}
                                          <Box sx={{ p: 1.5 }}>
                                            {(() => {
                                              let firstPageElements = [];
                                              let blogToUse = sourceBlog;

                                              if (!blogToUse || !blogToUse.content) {
                                                blogToUse = sourceBlogMap[blogIdStr] || null;
                                              }

                                              if (!blogToUse || !blogToUse.content) {
                                                const foundBlog = blogPosts.find(bp => {
                                                  const bpId = bp._id?.toString();
                                                  return bpId === blogIdStr;
                                                });
                                                if (foundBlog && foundBlog.content) {
                                                  blogToUse = foundBlog;
                                                }
                                              }

                                              if (blogToUse?.content) {
                                                try {
                                                  let content;
                                                  if (typeof blogToUse.content === 'string') {
                                                    // Only try to parse if it looks like JSON (starts with [ or {)
                                                    const trimmed = blogToUse.content.trim();
                                                    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                                                      content = JSON.parse(blogToUse.content);
                                                    } else {
                                                      // Not JSON, skip parsing
                                                      content = null;
                                                    }
                                                  } else {
                                                    content = blogToUse.content;
                                                  }

                                                  if (content && Array.isArray(content) && content.length > 0) {
                                                    const firstItem = content[0];

                                                    if (firstItem && firstItem.elements && Array.isArray(firstItem.elements)) {
                                                      firstPageElements = firstItem.elements;
                                                    } else if (firstItem && firstItem.type) {
                                                      firstPageElements = content.slice(0, 5);
                                                    } else if (Array.isArray(firstItem)) {
                                                      firstPageElements = firstItem;
                                                    }
                                                  }
                                                } catch (e) {
                                                  // Silently skip if content is not valid JSON
                                                  // This is expected for some blog formats
                                                }
                                              }

                                              const views = perfData?.count || perfData?.totalViews || 0;
                                              const clicks = perfData?.clicks || 0;
                                              const sales = perfData?.sales || 0;
                                              const revenue = perfData?.revenue || perfData?.totalRevenue || 0;

                                              const pageStats = blogPageStats[blogIdStr] || [];
                                              const offerStatsMap = new Map();

                                              pageStats.forEach(page => {
                                                if (page.offerId && page.offerName) {
                                                  const offerIdStr = page.offerId.toString();
                                                  if (!offerStatsMap.has(offerIdStr)) {
                                                    offerStatsMap.set(offerIdStr, {
                                                      offerId: offerIdStr,
                                                      offerName: page.offerName,
                                                      views: 0,
                                                      clicks: 0,
                                                      sales: 0,
                                                      revenue: 0
                                                    });
                                                  }
                                                  const offerStat = offerStatsMap.get(offerIdStr);
                                                  offerStat.views += page.views || 0;
                                                  offerStat.clicks += page.clicks || 0;
                                                  offerStat.sales += page.sales || 0;
                                                  offerStat.revenue += page.revenue || page.totalRevenue || 0;
                                                }
                                              });

                                              const offerStats = Array.from(offerStatsMap.values()).map(offer => {
                                                const ctr = offer.views > 0 ? ((offer.clicks / offer.views) * 100).toFixed(2) : '0.00';
                                                const cvr = offer.clicks > 0 ? ((offer.sales / offer.clicks) * 100).toFixed(2) : '0.00';
                                                const rpv = offer.views > 0 ? (offer.revenue / offer.views).toFixed(2) : '0.00';
                                                return {
                                                  ...offer,
                                                  ctr,
                                                  cvr,
                                                  rpv
                                                };
                                              });

                                              return (
                                                <Box>
                                                  <Box sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    mt: 1,
                                                    width: '100%',
                                                    overflowX: 'hidden',
                                                    overflowY: 'visible'
                                                  }}>
                                                    {/* Left: First Page Preview - Video or Image in square frame */}
                                                    {(() => {
                                                      const cardWidth = 400;
                                                      const previewWidth = Math.round(cardWidth / 4);
                                                      const previewHeight = previewWidth;

                                                      const mediaElement = firstPageElements.find(el =>
                                                        el.type === 'media' && el.mediaUrl
                                                      );

                                                      const isVideo = mediaElement?.mediaUrl?.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i);

                                                      return (
                                                        <Box sx={{
                                                          width: `${previewWidth}px`,
                                                          minWidth: `${previewWidth}px`,
                                                          flexShrink: 0,
                                                          border: '1px solid #e0e0e0',
                                                          borderRadius: 1,
                                                          p: 0,
                                                          backgroundColor: '#000',
                                                          height: `${previewHeight}px`,
                                                          maxHeight: `${previewHeight}px`,
                                                          overflow: 'hidden',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center'
                                                        }}>
                                                          {mediaElement ? (
                                                            isVideo ? (
                                                              <video
                                                                src={mediaElement.mediaUrl}
                                                                style={{
                                                                  width: '100%',
                                                                  height: '100%',
                                                                  objectFit: 'cover'
                                                                }}
                                                                controls
                                                              />
                                                            ) : (
                                                              <img
                                                                src={mediaElement.mediaUrl}
                                                                alt="Blog media"
                                                                style={{
                                                                  width: '100%',
                                                                  height: '100%',
                                                                  objectFit: 'cover'
                                                                }}
                                                              />
                                                            )
                                                          ) : (
                                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontSize: '0.6rem', color: '#fff' }}>
                                                              No media available
                                                            </Typography>
                                                          )}
                                                        </Box>
                                                      );
                                                    })()}

                                                    {/* Right: Performance Stats Table */}
                                                    <Box sx={{
                                                      flex: 1,
                                                      overflowX: 'hidden',
                                                      overflowY: 'visible',
                                                      maxWidth: 'calc(100% - 110px)'
                                                    }}>
                                                      <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { fontSize: '0.5rem', padding: '3px 2px', textAlign: 'center' } }}>
                                                        <TableHead>
                                                          <TableRow>
                                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '20%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Offer</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Views</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Clicks</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '12%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>CTR</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '15%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Sales</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '12%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>CVR</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '11%', fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>Rev</TableCell>
                                                          </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                          <TableRow>
                                                            <TableCell sx={{ fontWeight: 'medium', fontSize: '0.5rem', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>content</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{views.toLocaleString()}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                            <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>-</TableCell>
                                                          </TableRow>
                                                          {offerStats.slice(0, 2).map((offer, offerIndex) => (
                                                            <TableRow key={offerIndex}>
                                                              <TableCell sx={{ fontWeight: 'medium', fontSize: '0.5rem', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, textAlign: 'center' }} title={offer.offerName}>{offer.offerName}</TableCell>
                                                              <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.views.toLocaleString()}</TableCell>
                                                              <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.clicks.toLocaleString()}</TableCell>
                                                              <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.ctr}%</TableCell>
                                                              <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.sales.toLocaleString()}</TableCell>
                                                              <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>{offer.cvr}%</TableCell>
                                                              <TableCell sx={{ fontSize: '0.5rem', px: 0.5, textAlign: 'center' }}>${offer.revenue.toFixed(2)}</TableCell>
                                                            </TableRow>
                                                          ))}
                                                        </TableBody>
                                                      </Table>
                                                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                        <Button
                                                          size="small"
                                                          onClick={() => {
                                                            setExpandedStatsDialog({
                                                              blogId: blogIdStr,
                                                              blogTitle: blogToUse?.title || sourceBlog?.title || `Blog ${index + 1}`,
                                                              offerStats,
                                                              views,
                                                              clicks,
                                                              sales,
                                                              revenue
                                                            });
                                                          }}
                                                          endIcon={<OpenInNew />}
                                                          sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                                                        >
                                                          View Full Stats
                                                        </Button>
                                                      </Box>

                                                      {/* Pause, Remove, and Up/Down Buttons */}
                                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                                                        {/* Pause and Remove Buttons */}
                                                        {/* Pause and Remove Buttons */}
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          color="warning"
                                                          onClick={async () => {
                                                            const mergedBlogIdStr = mergedBlog._id?.toString();
                                                            const isPaused = pausedBlogs[mergedBlogIdStr]?.has(blogIdStr) || false;

                                                            setPausedBlogs(prev => {
                                                              const newState = { ...prev };
                                                              if (!newState[mergedBlogIdStr]) {
                                                                newState[mergedBlogIdStr] = new Set();
                                                              }

                                                              if (isPaused) {
                                                                newState[mergedBlogIdStr] = new Set(newState[mergedBlogIdStr]);
                                                                newState[mergedBlogIdStr].delete(blogIdStr);
                                                              } else {
                                                                newState[mergedBlogIdStr] = new Set(newState[mergedBlogIdStr]);
                                                                newState[mergedBlogIdStr].add(blogIdStr);
                                                              }

                                                              return newState;
                                                            });

                                                            try {
                                                              const mergedBlog = mergedBlogs.find(mb => mb._id?.toString() === mergedBlogIdStr);
                                                              if (!mergedBlog) {
                                                                showSnackbar('Snake not found', 'error');
                                                                return;
                                                              }

                                                              const currentPausedSet = pausedBlogs[mergedBlogIdStr] || new Set();
                                                              const newPausedSet = new Set(currentPausedSet);

                                                              if (isPaused) {
                                                                newPausedSet.delete(blogIdStr);
                                                              } else {
                                                                newPausedSet.add(blogIdStr);
                                                              }

                                                              const pausedBlogIdsArray = Array.from(newPausedSet);

                                                              await updateBlogPost(mergedBlogIdStr, {
                                                                pausedBlogIds: pausedBlogIdsArray
                                                              });

                                                              showSnackbar(isPaused ? 'Blog resumed' : 'Blog paused', 'success');

                                                              await reloadMergedBlog(mergedBlogIdStr);
                                                            } catch (error) {
                                                              console.error('Error updating paused state:', error);
                                                              showSnackbar('Failed to update paused state', 'error');
                                                              setPausedBlogs(prev => {
                                                                const revertedState = { ...prev };
                                                                if (!revertedState[mergedBlogIdStr]) {
                                                                  revertedState[mergedBlogIdStr] = new Set();
                                                                }
                                                                if (isPaused) {
                                                                  revertedState[mergedBlogIdStr] = new Set(revertedState[mergedBlogIdStr]);
                                                                  revertedState[mergedBlogIdStr].add(blogIdStr);
                                                                } else {
                                                                  revertedState[mergedBlogIdStr] = new Set(revertedState[mergedBlogIdStr]);
                                                                  revertedState[mergedBlogIdStr].delete(blogIdStr);
                                                                }
                                                                return revertedState;
                                                              });
                                                            }
                                                          }}
                                                          sx={{
                                                            textTransform: 'none',
                                                            minWidth: '70px',
                                                            fontSize: '0.7rem',
                                                            backgroundColor: pausedBlogs[mergedBlog._id?.toString()]?.has(blogIdStr) ? 'warning.light' : 'transparent',
                                                            borderColor: 'warning.main',
                                                            color: 'warning.main',
                                                            '&:hover': {
                                                              borderColor: 'warning.dark',
                                                              backgroundColor: 'warning.light',
                                                              color: 'warning.dark'
                                                            }
                                                          }}
                                                        >
                                                          {pausedBlogs[mergedBlog._id?.toString()]?.has(blogIdStr) ? 'Resume' : 'Pause'}
                                                        </Button>
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          color="error"
                                                          onClick={() => handleRemoveBlogFromMerged(mergedBlog._id, sourceBlogId)}
                                                          sx={{
                                                            textTransform: 'none',
                                                            minWidth: '70px',
                                                            fontSize: '0.7rem',
                                                            borderColor: 'error.main',
                                                            color: 'error.main',
                                                            '&:hover': {
                                                              borderColor: 'error.dark',
                                                              backgroundColor: 'error.light',
                                                              color: 'error.dark'
                                                            }
                                                          }}
                                                        >
                                                          Remove
                                                        </Button>

                                                        {/* Up/Down Arrows */}
                                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                          <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={index === 0}
                                                            onClick={async () => {
                                                              if (index > 0) {
                                                                const newOrder = [...sourceBlogIds];
                                                                [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                                                                await handleReorderBlogsInMerged(mergedBlog._id, newOrder);
                                                              }
                                                            }}
                                                            sx={{ p: 0.5 }}
                                                          >
                                                            <ArrowUpward fontSize="small" />
                                                          </IconButton>
                                                          <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={index === sourceBlogIds.length - 1}
                                                            onClick={async () => {
                                                              if (index < sourceBlogIds.length - 1) {
                                                                const newOrder = [...sourceBlogIds];
                                                                [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                                                                await handleReorderBlogsInMerged(mergedBlog._id, newOrder);
                                                              }
                                                            }}
                                                            sx={{ p: 0.5 }}
                                                          >
                                                            <ArrowDownward fontSize="small" />
                                                          </IconButton>
                                                        </Box>
                                                      </Box>
                                                    </Box>
                                                  </Box>
                                                </Box>
                                              );
                                            })()}
                                          </Box>
                                        </Card>
                                      );
                                    })}

                                    {/* Load More Blogs Button */}
                                    {hasMoreBlogs && (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setBlogsPerMergedBlogLimit(prev => ({
                                              ...prev,
                                              [mergedBlogIdStr]: (prev[mergedBlogIdStr] || 5) + 5
                                            }));
                                          }}
                                          endIcon={<ExpandMore />}
                                          sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                                        >
                                          Load {Math.min(5, sourceBlogIds.length - blogsLimit)} More Blogs
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}

                      {/* Load More Merged Blogs Button */}
                      {filteredMergedBlogs.length > mergedBlogsLimit && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setMergedBlogsLimit(prev => prev + 5);
                            }}
                            endIcon={<ExpandMore />}
                            sx={{ textTransform: 'none' }}
                          >
                            Load {Math.min(5, filteredMergedBlogs.length - mergedBlogsLimit)} More Snakes
                          </Button>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Date Range Picker Popover */}
        <Popover
          open={dateRangePickerOpen}
          anchorEl={dateRangePickerAnchor}
          onClose={() => setDateRangePickerOpen(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: { p: 2, minWidth: 700 }
          }}
        >
          <Box>
            {/* Header with Include Today and Quick Select */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 1.5, bgcolor: '#ffeb3b', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeToday}
                    onChange={(e) => setIncludeToday(e.target.checked)}
                    size="small"
                  />
                }
                label="Include today"
                sx={{ m: 0 }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const today = new Date();
                  const startOfYear = new Date(today.getFullYear(), 0, 1);
                  setTempStartDate(formatDateForInput(startOfYear));
                  setTempEndDate(formatDateForInput(today));
                  setStartMonth(startOfYear);
                  setEndMonth(today);
                }}
                sx={{ bgcolor: '#fff', borderColor: '#000', '&:hover': { bgcolor: '#f5f5f5' } }}
              >
                This year to date
                <KeyboardArrowDown sx={{ ml: 0.5 }} />
              </Button>
            </Box>

            {/* Two Calendar Grids */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Start Date Calendar */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Start Date</Typography>
                {(() => {
                  const daysInMonth = getDaysInMonth(startMonth);
                  const firstDay = getFirstDayOfMonth(startMonth);
                  const days = [];
                  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

                  for (let i = 0; i < firstDay; i++) {
                    days.push(null);
                  }

                  for (let i = 1; i <= daysInMonth; i++) {
                    days.push(i);
                  }

                  return (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {monthNames[startMonth.getMonth()]} {startMonth.getFullYear()}
                        </Typography>
                        <Box>
                          <IconButton size="small" onClick={() => {
                            const newMonth = new Date(startMonth);
                            newMonth.setMonth(newMonth.getMonth() - 1);
                            setStartMonth(newMonth);
                          }}>
                            <ChevronLeft fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => {
                            const newMonth = new Date(startMonth);
                            newMonth.setMonth(newMonth.getMonth() + 1);
                            setStartMonth(newMonth);
                          }}>
                            <ChevronRight fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                          <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold', p: 0.5 }}>
                            {day}
                          </Typography>
                        ))}
                        {days.map((day, idx) => {
                          if (day === null) {
                            return <Box key={idx} sx={{ height: 32 }} />;
                          }
                          const dateStr = formatDateForInput(new Date(startMonth.getFullYear(), startMonth.getMonth(), day));
                          const isSelected = tempStartDate && isSameDay(dateStr, tempStartDate);
                          const isInRange = tempStartDate && tempEndDate && isDateInRange(dateStr, tempStartDate, tempEndDate);

                          return (
                            <Button
                              key={idx}
                              size="small"
                              onClick={() => {
                                if (!tempStartDate || (tempStartDate && tempEndDate)) {
                                  setTempStartDate(dateStr);
                                  setTempEndDate(null);
                                } else if (tempStartDate && !tempEndDate) {
                                  if (new Date(dateStr) >= new Date(tempStartDate)) {
                                    setTempEndDate(dateStr);
                                  } else {
                                    setTempEndDate(tempStartDate);
                                    setTempStartDate(dateStr);
                                  }
                                }
                              }}
                              sx={{
                                minWidth: 32,
                                height: 32,
                                p: 0,
                                bgcolor: isSelected ? 'primary.main' : isInRange ? 'primary.light' : 'transparent',
                                color: isSelected ? 'white' : 'text.primary',
                                '&:hover': {
                                  bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                                },
                              }}
                            >
                              {day}
                            </Button>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>

              {/* End Date Calendar */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>End Date</Typography>
                {(() => {
                  const daysInMonth = getDaysInMonth(endMonth);
                  const firstDay = getFirstDayOfMonth(endMonth);
                  const days = [];
                  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

                  for (let i = 0; i < firstDay; i++) {
                    days.push(null);
                  }

                  for (let i = 1; i <= daysInMonth; i++) {
                    days.push(i);
                  }

                  return (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {monthNames[endMonth.getMonth()]} {endMonth.getFullYear()}
                        </Typography>
                        <Box>
                          <IconButton size="small" onClick={() => {
                            const newMonth = new Date(endMonth);
                            newMonth.setMonth(newMonth.getMonth() - 1);
                            setEndMonth(newMonth);
                          }}>
                            <ChevronLeft fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => {
                            const newMonth = new Date(endMonth);
                            newMonth.setMonth(newMonth.getMonth() + 1);
                            setEndMonth(newMonth);
                          }}>
                            <ChevronRight fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                          <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold', p: 0.5 }}>
                            {day}
                          </Typography>
                        ))}
                        {days.map((day, idx) => {
                          if (day === null) {
                            return <Box key={idx} sx={{ height: 32 }} />;
                          }
                          const dateStr = formatDateForInput(new Date(endMonth.getFullYear(), endMonth.getMonth(), day));
                          const isSelected = tempEndDate && isSameDay(dateStr, tempEndDate);
                          const isInRange = tempStartDate && tempEndDate && isDateInRange(dateStr, tempStartDate, tempEndDate);

                          return (
                            <Button
                              key={idx}
                              size="small"
                              onClick={() => {
                                if (!tempStartDate || (tempStartDate && tempEndDate)) {
                                  setTempStartDate(dateStr);
                                  setTempEndDate(null);
                                } else if (tempStartDate && !tempEndDate) {
                                  if (new Date(dateStr) >= new Date(tempStartDate)) {
                                    setTempEndDate(dateStr);
                                  } else {
                                    setTempEndDate(tempStartDate);
                                    setTempStartDate(dateStr);
                                  }
                                }
                              }}
                              sx={{
                                minWidth: 32,
                                height: 32,
                                p: 0,
                                bgcolor: isSelected ? 'primary.main' : isInRange ? 'primary.light' : 'transparent',
                                color: isSelected ? 'white' : 'text.primary',
                                '&:hover': {
                                  bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                                },
                              }}
                            >
                              {day}
                            </Button>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button onClick={() => setDateRangePickerOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  let finalStartDate = tempStartDate;
                  let finalEndDate = tempEndDate;

                  if (includeToday && finalEndDate) {
                    const today = formatDateForInput(new Date());
                    if (new Date(today) > new Date(finalEndDate)) {
                      finalEndDate = today;
                    }
                  }

                  setMergedBlogsStartDate(finalStartDate || '');
                  setMergedBlogsEndDate(finalEndDate || '');
                  setDateRangePickerOpen(false);
                }}
                sx={{ bgcolor: '#ffeb3b', color: '#000', '&:hover': { bgcolor: '#fdd835' } }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Expanded Stats Dialog */}
        <Dialog
          open={expandedStatsDialog !== null}
          onClose={() => setExpandedStatsDialog(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Full Stats: {expandedStatsDialog?.blogTitle || 'Blog Stats'}
          </DialogTitle>
          <DialogContent>
            <Table size="small" sx={{ width: '100%', tableLayout: 'auto', mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Offer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Views</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Clicks</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">CTR</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Sales</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">CVR</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">Revenue</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">RPV</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Overall Blog Stats Row */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'medium' }}>content</TableCell>
                  <TableCell align="right">{expandedStatsDialog?.views?.toLocaleString() || '0'}</TableCell>
                  <TableCell align="right">{expandedStatsDialog?.clicks?.toLocaleString() || '-'}</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">{expandedStatsDialog?.sales?.toLocaleString() || '-'}</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">{expandedStatsDialog?.revenue ? `$${expandedStatsDialog.revenue.toFixed(2)}` : '-'}</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
                {/* Offer-level stats rows */}
                {expandedStatsDialog?.offerStats?.map((offer, offerIndex) => (
                  <TableRow key={offerIndex}>
                    <TableCell sx={{ fontWeight: 'medium' }}>{offer.offerName}</TableCell>
                    <TableCell align="right">{offer.views.toLocaleString()}</TableCell>
                    <TableCell align="right">{offer.clicks.toLocaleString()}</TableCell>
                    <TableCell align="right">{offer.ctr}%</TableCell>
                    <TableCell align="right">{offer.sales.toLocaleString()}</TableCell>
                    <TableCell align="right">{offer.cvr}%</TableCell>
                    <TableCell align="right">${offer.revenue.toFixed(2)}</TableCell>
                    <TableCell align="right">${offer.rpv}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExpandedStatsDialog(null)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default Blog; 